// @flow
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bemBlock } from '../../core/render/visualUtils';
import type { Device } from '../../library/model/Device.model';

export type SearchDeviceWidgetViewProps = {
  searchTerm: string,
  devices: Array<Device>,
  expanded: boolean,
  loading: boolean,
  selectedDeviceId: ?string,
  onSetExpanded: boolean => void,
  onType: string => void,
  onMove: (delta: -1 | 1) => void,
  onEnter: () => void,
  onSelectResult: () => void,
}

export default function SearchDeviceWidgetView({
  searchTerm,
  devices,
  expanded,
  loading,
  selectedDeviceId,
  onSetExpanded,
  onType,
  onMove,
  onEnter,
  onSelectResult,
}: SearchDeviceWidgetViewProps) {
  const { t } = useTranslation();
  const bem = bemBlock('search-device');
  const dropdownRef = useRef<?HTMLElement>(null);

  const showDropdown = expanded && (loading || devices.length > 0 || searchTerm.length > 0);

  return (
    <div className={bem()}>
      <input
        className={'form-control ' + bem('box')}
        placeholder="Поиск устройства"
        value={searchTerm}
        onFocus={() => onSetExpanded(true)}
        onBlur={(e) => {
          if (!dropdownRef.current || !e.relatedTarget || !dropdownRef.current.contains(e.relatedTarget)) {
            onSetExpanded(false);
          }
        }}
        onChange={e => onType(e.target.value)}
        onKeyDown={(e) => {
          if (e.keyCode === 38) { onMove(-1); e.preventDefault(); }
          if (e.keyCode === 40) { onMove(1); e.preventDefault(); }
          if (e.keyCode === 13) { onEnter(); e.target.blur(); e.preventDefault(); }
          if (e.keyCode === 27) { onSetExpanded(false); e.target.blur(); }
        }}
      />
      {showDropdown && (
        <div className={bem('dropdown-wrapper')} ref={dropdownRef}>
          <div className={bem('dropdown')}>
            {loading && (
              <div className={'d-flex align-items-center justify-content-center py-3 ' + bem('loading')}>
                <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                  <span className="sr-only">Загрузка…</span>
                </div>
                <span className="text-muted">Поиск…</span>
              </div>
            )}
            {!loading && devices.length === 0 && searchTerm.length > 0 && (
              <div className={'py-3 px-3 text-muted text-center ' + bem('empty')}>
                Ничего не найдено
              </div>
            )}
            {!loading && devices.map(({
              id,
              name,
              preview,
              brand,
            }: Device) => (
              <Link
                to={`/frontdesk/${brand.id}/${id}/`}
                key={id}
                className={
                  'd-flex align-items-center '
                  + bem('item', { selected: selectedDeviceId === id })
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => onSelectResult()}
              >
                <span
                  className={bem('pic')}
                  style={{ backgroundImage: preview && preview.fit ? `url(${preview.fit.url})` : null }}
                />
                <span>
                  {brand.name} <strong>{name}</strong>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}